import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { RecoveryPasswordComponent } from 'src/app/auth/recovery-password/recovery-password.component';
import { ResetPasswordComponent } from 'src/app/auth/reset-password/reset-password.component';
import {
  connectionPageTitleResolver,
  doctorPageTitleResolver,
  filePageTitleResolver,
  PagesTitle,
  patientPageTitleResolver,
} from './app-pages-title/app-pages-title';
import { AcceptAdminSignupInvitationComponent } from './auth/accept-signup-invitation/accept-admin-signup-invitation/accept-admin-signup-invitation.component';
import { BindAuthenticatorComponent } from './auth/bind-authenticator/bind-authenticator.component';
import { ChangePasswordComponent } from './auth/change-password/change-password.component';
import { LoginComponent } from './auth/login/login.component';
import { RegistrationComponent } from './auth/registration/registration.component';
import { SecondAuthLevelComponent } from './auth/second-auth-level/second-auth-level.component';
import { AdminSessionAuthLevelGuard } from './common/guards/admin-session-auth-level/admin-session-auth-level.guard';
import { AuthGuard } from './common/guards/auth/auth.guard';
import { DoctorRoleGuard, PatientRoleGuard } from './common/guards/role-level-guard/role-level.guard';
import { SaveDataGuard } from './common/guards/save-data-guard/save-data.guard';
import { SecondAuthLevelGuard } from './common/guards/second-auth-level/second-auth-level.guard';
import { UserNotLoggedGuard } from './common/guards/user-logged/user-logged.guard';
import ProjectLinks from './common/project.links';
import { AdminsComponent } from './components/admin/admins/admins.component';
import {
  CATEGORY_ROUTES,
  CHARITY_FOCUS_ROUTE,
  DISEASES_ROUTE,
  SPECIALTIES_ROUTE,
} from './components/admin/category/category-helpers';
import {
  CategoriesApi,
  CategoriesStore,
  CharityFocusesApi,
  DiseasesApi,
  SpecialtiesApi,
} from './components/admin/category/category.service';
import { ManageCategoriesComponent } from './components/admin/category/manage-categories/manage-categories.component';
import { DeactivatedUsersComponent } from './components/admin/deactivated-users/deactivated-users.component';
import { BlockedUsersComponent } from './components/chat/blocked-users/blocked-users.component';
import { ConversationsChatComponent } from './components/chat/conversations-chat.component';
import {
  CHARITY_COMMUNITY_PROVIDERS,
  CHARITY_COMMUNITY_ROUTE_PROVIDERS,
  CommunityView,
  HEALTH_COMMUNITY_PROVIDERS,
  HEALTH_COMMUNITY_ROUTE_PROVIDERS,
} from './components/community/communities-store';
import { CommunitiesComponent } from './components/community/communities/communities.component';
import { CommunityRouteService } from './components/community/community-route.service';
import { CreateCommunityComponent } from './components/community/create-community/create-community.component';
import { DetailsCommunityComponent } from './components/community/details-community/details-community.component';
import { ManageCommunityComponent } from './components/community/manage-community/manage-community.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { FileDetailsComponent } from './components/files/file-details/file-details.component';
import { FileManagerComponent } from './components/files/file-manager/file-manager.component';
import { FilesRootComponent } from './components/files/files-root.component';
import { MapComponent } from './components/map/map.component';
import { postsRoutes } from './components/posts/posts-routing.module';
import { DistributionPreferencesComponent } from './components/preferences/distribution-preferences/distribution-preferences.component';
import { NotificationPreferences } from './components/preferences/notification-preferences/notification-preferences.component';
import { PreferencesComponent } from './components/preferences/preferences.component';
import { EditProfileComponent } from './components/profile/user-profile/edit-profile/edit-profile.component';
import { UserProfileComponent } from './components/profile/user-profile/user-profile.component';
import { ViewProfileComponent } from './components/profile/user-profile/view-profile/view-profile.component';
import { CreateProjectComponent } from './components/projects/create-project/create-project.component';
import { ManageProjectService } from './components/projects/manage-project.service';
import { ManageProjectComponent } from './components/projects/project-detail-info/manage-project/manage-project.component';
import { ProjectDetailInfoComponent } from './components/projects/project-detail-info/project-detail-info.component';
import { ProjectRouteService } from './components/projects/project-route.service';
import { ProjectsNavigationComponent } from './components/projects/projects-navigation.component';
import { UserProjectsComponent } from './components/projects/user-projects/user-projects.component';
import { BasicSearchComponent } from './components/search/search-component/basic-search/basic-search.component';
import { InviteToSocialGroupSearchComponent } from './components/search/search-component/invite-to-social-group/invite-to-social-group-page/invite-to-social-group-search.component';
import {
  InviteToSocialGroupRouteService,
  socialGroupEntityResolver,
} from './components/search/search-component/invite-to-social-group/social-group-resolver';
import { SocialInitiative } from './components/shared/social-initiative/social-initiative.abstract';
import { AboutUsComponent } from './components/static/about-us/about-us.component';
import { DonateComponent } from './components/static/donate/donate.component';
import { TermsComponent } from './components/static/terms/terms/terms.component';
import { ConnectionsPageComponent } from './components/user-relation-requests/components/connections-page/connections-page.component';
import { DoctorLinksPageComponent } from './components/user-relation-requests/components/doctor-links-page/doctor-links-page.component';
import { RelatedUserMessageComponent } from './components/user-relation-requests/components/group-messaging-page/user-group-messaging.component';
import { ManageRelationRequestComponent } from './components/user-relation-requests/components/manage-relation-request/manage-relation-request.component';
import { PatientLinksPageComponent } from './components/user-relation-requests/components/patient-links-page/patient-links-page.component';
import { editEntityResolver, RouteService, viewEntityResolver } from './services/route-service.abstract';
import { Project } from './swagger-api';
import { createProvider } from './utils';

const routes: Routes = [
  {
    path: 'chat',
    title: PagesTitle.CHAT_PAGE,
    component: ConversationsChatComponent,
    canDeactivate: [SaveDataGuard],
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'chat/:id',
    title: PagesTitle.CHAT_PAGE,
    component: ConversationsChatComponent,
    canDeactivate: [SaveDataGuard],
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'blocked-users',
    title: PagesTitle.BLOCKED_PAGE,
    component: BlockedUsersComponent,
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'login',
    title: PagesTitle.LOGIN_PAGE,
    component: LoginComponent,
    canActivate: [UserNotLoggedGuard],
  },
  {
    path: 'reset-password',
    title: PagesTitle.RESET_PASSWORD_PAGE,
    component: ResetPasswordComponent,
    canActivate: [UserNotLoggedGuard],
  },
  {
    path: 'password_recovery',
    title: PagesTitle.PASSWORD_RECOVERY_PAGE,
    component: RecoveryPasswordComponent,
  },
  {
    path: 'change-password',
    title: PagesTitle.CHANGE_PASSWORD_PAGE,
    component: ChangePasswordComponent,
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
    canDeactivate: [SaveDataGuard],
  },
  {
    path: 'sign-up',
    title: PagesTitle.SIGN_UP_PAGE,
    component: RegistrationComponent,
    canActivate: [UserNotLoggedGuard],
  },
  {
    path: 'accept-admin-signup-invitation',
    title: PagesTitle.ACCEPT_ADMIN_SIGN_UP_INVITATION_PAGE,
    component: AcceptAdminSignupInvitationComponent,
  },
  {
    path: 'second-auth-level',
    title: PagesTitle.SECOND_AUTH_LEVEL_PAGE,
    component: SecondAuthLevelComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'bind-authenticator',
    title: PagesTitle.BIND_AUTHENTICATOR_PAGE,
    component: BindAuthenticatorComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admins',
    title: PagesTitle.ADMINS_LIST_PAGE,
    component: AdminsComponent,
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'profile',
    component: UserProfileComponent,
    children: [
      { path: '', redirectTo: 'view', pathMatch: 'full' },
      { path: 'view', component: ViewProfileComponent, title: PagesTitle.VIEW_PROFILE_PAGE },
      {
        path: 'view/:id',
        component: ViewProfileComponent,
        title: PagesTitle.VIEW_PROFILE_PAGE,
        canDeactivate: [SaveDataGuard],
      },
      {
        path: 'edit',
        title: PagesTitle.EDIT_PROFILE_PAGE,
        component: EditProfileComponent,
        canDeactivate: [SaveDataGuard],
      },
    ],
    canActivate: [SecondAuthLevelGuard],
  },
  {
    path: 'files',
    component: FilesRootComponent,
    children: [
      { path: '', redirectTo: 'manager', pathMatch: 'full' },
      {
        path: ':tab',
        title: filePageTitleResolver,
        component: FileManagerComponent,
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        canDeactivate: [SaveDataGuard],
      },
      { path: 'details/:id', component: FileDetailsComponent },
    ],
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
    canActivateChild: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'connections',
    component: ConnectionsPageComponent,
    children: [
      { path: '', redirectTo: 'list', pathMatch: 'full' },
      {
        path: ':type',
        title: connectionPageTitleResolver,
        component: ManageRelationRequestComponent,
        canDeactivate: [SaveDataGuard],
      },
      {
        path: 'list/group-message',
        title: PagesTitle.MESSAGE_TO_GROUP_PAGE,
        component: RelatedUserMessageComponent,
        pathMatch: 'full',
        data: {
          breadcrumbs: {
            link: ProjectLinks.LINK_CONNECTIONS_LIST,
            pageTitle: 'SEND_MESSAGE_TO_GROUP_HINT',
            testId: 'send-message-to-group-title',
            previousPageTitle: 'MY_CONNECTIONS_TITLE',
          },
        },
      },
    ],
    canActivate: [AdminSessionAuthLevelGuard],
    canActivateChild: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'doctor-links',
    component: DoctorLinksPageComponent,
    children: [
      { path: '', redirectTo: 'list', pathMatch: 'full' },
      {
        path: ':type',
        title: doctorPageTitleResolver,
        component: ManageRelationRequestComponent,
        canDeactivate: [SaveDataGuard],
      },
    ],

    canActivate: [AdminSessionAuthLevelGuard, PatientRoleGuard],
    canActivateChild: [AdminSessionAuthLevelGuard, PatientRoleGuard],
  },
  {
    path: 'patient-links',
    component: PatientLinksPageComponent,
    children: [
      { path: '', redirectTo: 'list', pathMatch: 'full' },
      {
        path: ':type',
        title: patientPageTitleResolver,
        component: ManageRelationRequestComponent,
        canDeactivate: [SaveDataGuard],
      },
    ],
    canActivate: [AdminSessionAuthLevelGuard, DoctorRoleGuard],
    canActivateChild: [AdminSessionAuthLevelGuard, DoctorRoleGuard],
  },
  { path: 'map', redirectTo: 'map/communities', pathMatch: 'full' },
  { path: 'map/:entity', title: PagesTitle.MAP_PAGE, component: MapComponent },
  { path: 'search', title: PagesTitle.SEARCH_PAGE, component: BasicSearchComponent },
  {
    path: 'invite-to-community/:id',
    component: InviteToSocialGroupSearchComponent,
    providers: HEALTH_COMMUNITY_PROVIDERS,
    resolve: { entity: socialGroupEntityResolver<CommunityView>() },
    canActivate: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'invite-to-charity/:id',
    component: InviteToSocialGroupSearchComponent,
    providers: CHARITY_COMMUNITY_PROVIDERS,
    resolve: { entity: socialGroupEntityResolver<CommunityView>() },
    canActivate: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'invite-to-project/:id',
    component: InviteToSocialGroupSearchComponent,
    providers: [{ provide: SocialInitiative, useClass: ManageProjectService }, InviteToSocialGroupRouteService],
    resolve: { entity: socialGroupEntityResolver<Project>() },
    canActivate: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'projects',
    canActivate: [AdminSessionAuthLevelGuard],
    canActivateChild: [AdminSessionAuthLevelGuard],
    providers: [createProvider(RouteService, { useClass: ProjectRouteService })],
    children: [
      {
        path: '',
        component: ProjectsNavigationComponent,
        children: [
          { path: '', redirectTo: 'all', pathMatch: 'full' },
          { path: 'all', component: UserProjectsComponent, title: PagesTitle.PROJECTS_LIST_PAGE },
          {
            path: 'create',
            component: CreateProjectComponent,
            title: PagesTitle.CREATE_PROJECT_PAGE,
            pathMatch: 'full',
            canDeactivate: [SaveDataGuard],
          },
          {
            path: 'view/:id',
            component: ProjectDetailInfoComponent,
            resolve: { project: viewEntityResolver<Project>() },
          },
          {
            path: 'manage/:id',
            component: ManageProjectComponent,
            resolve: { project: editEntityResolver<Project>() },
            canDeactivate: [SaveDataGuard],
          },
        ],
      },
    ],
  },
  {
    path: 'community',
    providers: [...HEALTH_COMMUNITY_ROUTE_PROVIDERS, { provide: RouteService, useClass: CommunityRouteService }],
    children: [
      { path: '', redirectTo: 'all', pathMatch: 'full' },
      { path: 'all', component: CommunitiesComponent, title: PagesTitle.COMMUNITIES_LIST_PAGE },
      {
        path: 'create',
        component: CreateCommunityComponent,
        title: PagesTitle.CREATE_COMMUNITY_PAGE,
        pathMatch: 'full',
        canDeactivate: [SaveDataGuard],
      },
      {
        path: 'view/:id',
        component: DetailsCommunityComponent,
        resolve: { community: viewEntityResolver<CommunityView>() },
      },
      {
        path: 'manage/:id',
        component: ManageCommunityComponent,
        resolve: { community: editEntityResolver<CommunityView>() },
        canDeactivate: [SaveDataGuard],
      },
    ],
    canActivate: [AdminSessionAuthLevelGuard],
    canActivateChild: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'charity',
    providers: [...CHARITY_COMMUNITY_ROUTE_PROVIDERS, { provide: RouteService, useClass: CommunityRouteService }],
    children: [
      {
        path: 'create',
        component: CreateCommunityComponent,
        title: PagesTitle.CREATE_COMMUNITY_PAGE,
        pathMatch: 'full',
        canDeactivate: [SaveDataGuard],
        canActivate: [AdminSessionAuthLevelGuard],
      },
      {
        path: 'view/:id',
        component: DetailsCommunityComponent,
        resolve: { community: viewEntityResolver<CommunityView>() },
      },
      {
        path: 'manage/:id',
        component: ManageCommunityComponent,
        resolve: { community: editEntityResolver<CommunityView>() },
        canDeactivate: [SaveDataGuard],
        canActivate: [AdminSessionAuthLevelGuard],
      },
    ],
  },
  {
    path: 'posts',
    children: postsRoutes,
    canActivate: [AdminSessionAuthLevelGuard],
    canActivateChild: [AdminSessionAuthLevelGuard],
  },
  {
    path: 'deactivated-users',
    title: PagesTitle.DEACTIVATED_USER_PAGE,
    component: DeactivatedUsersComponent,
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: CHARITY_FOCUS_ROUTE.path,
    title: PagesTitle.CHARITY_FOCUSES_PAGE,
    component: ManageCategoriesComponent,
    providers: [createProvider(CategoriesApi, { useClass: CharityFocusesApi }), CategoriesStore],
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'categories',
    providers: [createProvider(CATEGORY_ROUTES, { useValue: [DISEASES_ROUTE, SPECIALTIES_ROUTE] })],
    children: [
      {
        path: '',
        redirectTo: DISEASES_ROUTE.path,
        pathMatch: 'full',
      },
      {
        path: DISEASES_ROUTE.path,
        title: PagesTitle.DISEASES_AND_SPECIALTIES_PAGE,
        component: ManageCategoriesComponent,
        providers: [createProvider(CategoriesApi, { useClass: DiseasesApi }), CategoriesStore],
      },
      {
        path: SPECIALTIES_ROUTE.path,
        title: PagesTitle.DISEASES_AND_SPECIALTIES_PAGE,
        component: ManageCategoriesComponent,
        providers: [createProvider(CategoriesApi, { useClass: SpecialtiesApi }), CategoriesStore],
      },
    ],
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
    canActivateChild: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
  },
  {
    path: 'preferences',
    title: PagesTitle.PREFERENCES_PAGE,
    component: PreferencesComponent,
    canActivate: [SecondAuthLevelGuard, AdminSessionAuthLevelGuard],
    canDeactivate: [SaveDataGuard],
    children: [
      {
        path: '',
        redirectTo: 'notifications',
        pathMatch: 'full',
      },
      {
        path: 'notifications',
        component: NotificationPreferences,
        canDeactivate: [SaveDataGuard],
      },
      {
        path: 'distribution',
        component: DistributionPreferencesComponent,
        canDeactivate: [SaveDataGuard],
      },
    ],
  },
  { path: 'about-us', title: PagesTitle.ABOUT_US_PAGE, component: AboutUsComponent },
  { path: 'terms', title: PagesTitle.TERMS_PAGE, component: TermsComponent },
  { path: 'donate', title: PagesTitle.DONATE_PAGE, component: DonateComponent },
  { path: 'contact-us', title: PagesTitle.CONTACT_US_PAGE, component: ContactUsComponent },
  { path: '**', redirectTo: 'map/communities' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      onSameUrlNavigation: 'reload',
      anchorScrolling: 'enabled',
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
